import api, { ResponsePayloadWithData } from '.';
import { Group } from './groups';
import { replaceSoftHyphenWithUnicode } from '../utils/replaceSoftHyphenWithUnicode';

export interface GroupModule {
  id: string;
  catalogName: string;
  years: string[];
  image?: string;
}

export interface GroupModuleYearUnwind extends Omit<GroupModule, 'years'> {
  year: string;
}

export interface CreateGroupRequestPayload {
  schoolYear: string;
  name: string;
  school: {
    id: string;
    isSmartschool: boolean;
  };
  subjects: string[];
}

type CreateGroupQuery = {
  studioGroup?: StudioGroup;
};

function unwindYearInModule({ years, ...module }: GroupModule): GroupModuleYearUnwind {
  return {
    ...module,
    catalogName: replaceSoftHyphenWithUnicode(module.catalogName),
    year: years[0],
  };
}

export async function fetchLinkedStudentModules(groupId: string) {
  const { data } = await api.get<GroupModule[]>(`/studio/user/groups/${groupId}/modules`);

  return data.map(unwindYearInModule);
}

export async function fetchLicensedStudentModules() {
  const { data } = await api.get<GroupModule[]>(`/studio/user/groups/licensed-modules`, {
    params: { studioGroup: process.env.REACT_APP_P_STUDIO_GROUP },
  });

  return data.map(unwindYearInModule);
}

export async function createGroup(
  { name, school, schoolYear, subjects }: CreateGroupRequestPayload,
  queryObj: CreateGroupQuery = {},
): Promise<Group> {
  const query = new URLSearchParams(queryObj);

  const { data } = await api.post<Group>(`/studio/user/school-years/${schoolYear}/groups?${query}`, {
    name,
    school,
    subjects,
  });

  return data;
}

export async function linkStudentModuleToGroup(groupId: string, modulesToLink: string[]) {
  const { data } = await api.post<string[]>(`/studio/user/groups/${groupId}/modules`, modulesToLink);

  return data;
}

export async function unlinkStudentModuleFromGroup(groupId: string, moduleId: string) {
  const { data } = await api.delete<string[]>(`/studio/user/groups/${groupId}/modules/${moduleId}`);

  return data;
}

export interface RegisterMemberRequestPayload {
  firstName: string;
  lastName: string;
  email?: string;
  username: string;
  password: string;
}

export async function registerMembers(groupId: string, members: RegisterMemberRequestPayload[]) {
  const { data } = await api.post<ResponsePayloadWithData<RegisterMemberRequestPayload[]>>(
    `/studio/user/groups/${groupId}/members`,
    { data: members },
  );

  return data.data;
}

export type RegisterOrEditUserResponsePayload = RegisterMemberRequestPayload & {
  id: string;
};

export async function editMember(groupId: string, studentId: string, editInfo: RegisterMemberRequestPayload) {
  const {
    data: { data },
  } = await api.put<ResponsePayloadWithData<RegisterOrEditUserResponsePayload[]>>(
    `/studio/user/groups/${groupId}/members/${studentId}`,
    editInfo,
  );

  return data;
}
